import type { ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import type { WorkflowStepId } from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import { STEP_SEARCH_PARAM } from './workflowStepParam';

/**
 * Renders `children` for a URL that names a workflow step, and requests
 * `step` for one that names none — replacing the entry, so Back does not
 * return to the bare URL and bounce forward again.
 *
 * For a context whose external entry point addresses the context alone and
 * still means a particular step. The request is an ordinary `step` parameter:
 * it takes effect as soon as the step's data has arrived, like any other.
 */
export default function RequestStepIfUnnamed({
    step,
    children,
}: {
    step: WorkflowStepId;
    children: ReactNode;
}) {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    if (searchParams.has(STEP_SEARCH_PARAM)) {
        return <>{children}</>;
    }
    const requested = new URLSearchParams(searchParams);
    requested.set(STEP_SEARCH_PARAM, step);
    return (
        <Navigate
            to={{
                pathname: location.pathname,
                search: `?${requested.toString()}`,
                hash: location.hash,
            }}
            replace
        />
    );
}
